import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Zap,
  CheckCircle2,
  ShieldCheck,
  Share2,
  Heart,
  Star,
  Settings,
  Users,
  Battery,
  Calendar,
  MapPin,
  ZoomIn,
  ZoomOut,
  X
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import PricingBreakdownModal from '../components/ui/PricingBreakdownModal';
import CarEnquiry from '../components/ui/CarEnquiry';
import { carPath } from '../utils/listingUrl';
import { trackDiscovery } from '../services/discovery.service';
import SeoHead from '../components/seo/SeoHead';
import { publicService } from '../services/public.service';
import { renterService } from '../services/renter.service';
import { getDefaultDailyPrice, normalizePricingConfigurations, resolveAppliedPricing } from '../utils/pricing';
import { showAppAlert } from '../utils/appAlert';
import { shareCar } from '../utils/shareCar';
import { buildPublicFileUrl } from '../utils/fileUrl';

const formatCurrency = (amount) => `RF ${Math.round(amount || 0).toLocaleString()}`;
const getTodayDateValue = () => new Date().toISOString().split('T')[0];
const getRentalDays = (pickupDate, returnDate) => {
  if (!pickupDate || !returnDate) return 0;
  const start = new Date(pickupDate);
  const end = new Date(returnDate);
  const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  return diff >= 0 ? Math.max(1, diff) : 0;
};

const buildImageUrl = (imageUrl) => {
  if (!imageUrl) return 'https://images.unsplash.com/photo-1555215695-3004980ad54e?auto=format&fit=crop&q=80&w=1200';
  return buildPublicFileUrl(imageUrl);
};

const mapApiCarToViewModel = (car) => ({
  id: car.id,
  name: `${car.make || 'Premium'} ${car.model || 'Vehicle'}`.trim(),
  year: car.year,
  price: getDefaultDailyPrice(car.pricingConfigurations, car.pricePerDay || 0),
  image: buildImageUrl(car.primaryImageUrl),
  images: [car.primaryImageUrl, ...(car.imageUrls || [])]
    .filter(Boolean)
    .map(buildImageUrl)
    .filter((value, index, array) => array.indexOf(value) === index),
  category: car.categoryName || 'Premium',
  cityName: car.cityName,
  transmission: car.transmission || 'Automatic',
  seats: car.seats || 5,
  type: car.fuelType || 'Fuel',
  allowOutsideCity: Boolean(car.allowOutsideCity),
  outsideCityPrice: car.outsideCityPrice || 0,
  serviceFee: Number(car.serviceFee || 0),
  longTermDiscount: car.longTermDiscount || 0,
  pricingConfigurations: normalizePricingConfigurations(car.pricingConfigurations || []),
  engine: car.fuelType || 'Standard',
  about: car.description || 'No vehicle description has been added yet.',
  specificationGroups: (car.specificationGroups || [])
    .map((group) => ({
      label: group.label || 'Vehicle Features',
      items: (group.items || []).filter(Boolean)
    }))
    .filter((group) => group.items.length > 0),
  rentalTerms: car.rentalTerms || [],
  averageRating: Number(car.averageRating || 0),
  reviewCount: Number(car.reviewCount || 0),
  reviews: car.reviews || [],
});

const CarDetails = () => {
  const { id } = useParams();
  useEffect(() => { trackDiscovery('car_view',id); },[id]);
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [bookingDetails, setBookingDetails] = useState({
    pickupCityId: '',
    destinationLocation: '',
    pickupDate: '',
    returnDate: '',
    outsideCity: false
  });
  const [bookingError, setBookingError] = useState('');
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [wishlistBusy, setWishlistBusy] = useState(false);
  const [reviewEligibility, setReviewEligibility] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    reviewText: ''
  });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [showPricingBreakdown, setShowPricingBreakdown] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);
  const [zoomOrigin, setZoomOrigin] = useState('50% 50%');
  const isRenter = !!localStorage.getItem('token');

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await publicService.getCities();
        setCities(response);
      } catch (error) {
        setCities([]);
      }
    };

    fetchCities();
  }, []);

  useEffect(() => {
    const fetchCar = async () => {
      try {
        const apiCar = await publicService.getCarById(id);
        setCar(mapApiCarToViewModel(apiCar));
      } catch (error) {
        navigate('/browse', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    fetchCar();
  }, [id, navigate]);

  useEffect(() => {
    const loadWishlistState = async () => {
      if (!isRenter) {
        setIsWishlisted(false);
        return;
      }

      try {
        const ids = await renterService.getWishlistIds();
        setIsWishlisted((ids || []).includes(id));
      } catch (error) {
        setIsWishlisted(false);
      }
    };

    loadWishlistState();
  }, [id, isRenter]);

  useEffect(() => {
    const loadReviewEligibility = async () => {
      if (!isRenter) {
        setReviewEligibility(null);
        return;
      }

      try {
        const response = await renterService.getReviewEligibility(id);
        setReviewEligibility(response);
      } catch (error) {
        setReviewEligibility(null);
      }
    };

    loadReviewEligibility();
  }, [id, isRenter]);

  useEffect(() => {
    if (!bookingDetails.pickupCityId && cities.length > 0) {
      const matchingCity = cities.find((city) => city.name === car?.cityName);
      setBookingDetails((current) => ({
        ...current,
        pickupCityId: matchingCity?.id || cities[0].id,
        destinationLocation: current.destinationLocation || ''
      }));
    }
  }, [cities, bookingDetails.pickupCityId, car]);

  useEffect(() => {
    setActiveImageIndex(0);
    setZoomOpen(false);
    setZoomScale(1);
    setZoomOrigin('50% 50%');
  }, [car?.id]);

  useEffect(() => {
    if (!zoomOpen) {
      return undefined;
    }

    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setZoomOpen(false);
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [zoomOpen]);

  const rentalDays = (!bookingDetails.pickupDate && !bookingDetails.returnDate)
    ? 1
    : getRentalDays(bookingDetails.pickupDate, bookingDetails.returnDate);
  const { appliedConfig, basePrice, breakdown } = resolveAppliedPricing(car?.pricingConfigurations, rentalDays, car?.price || 0);
  const serviceFee = Number(car?.serviceFee || 0);
  const longTermDiscount = 0;
  const outsideCityFee = bookingDetails.outsideCity ? (car?.outsideCityPrice || 0) : 0;
  const totalPrice = basePrice + serviceFee + outsideCityFee - longTermDiscount;
  const todayDateValue = getTodayDateValue();

  const validateBookingDates = () => {
    if (!bookingDetails.pickupDate || !bookingDetails.returnDate) {
      return 'Please select your destination location and rental dates.';
    }

    if (bookingDetails.pickupDate < todayDateValue) {
      return 'Pickup date cannot be before today.';
    }

    if (bookingDetails.returnDate < bookingDetails.pickupDate) {
      return 'Return date cannot be before pickup date.';
    }

    return '';
  };

  const handleBookNow = () => {
    const resolvedCityId = bookingDetails.pickupCityId;

    const dateValidationError = validateBookingDates();
    if (!bookingDetails.destinationLocation.trim()) {
      setBookingError('Please type your destination location.');
      return;
    }

    if (!resolvedCityId || dateValidationError) {
      setBookingError(dateValidationError || 'Please select your destination location and rental dates.');
      return;
    }

    setBookingError('');
    trackDiscovery('booking_start',car.id);
    navigate(`/checkout/${car.id}`, {
      state: {
        bookingDetails: {
          ...bookingDetails,
          pickupCityId: resolvedCityId
        },
        pricing: {
          rentalDays,
          basePrice,
          outsideCityFee,
          longTermDiscount,
          appliedConfig,
          serviceFee,
          totalPrice
        }
      }
    });
  };

  const handleShare = async () => {
    await shareCar({ carId: car.id, carName: `${car.year} ${car.name}` });
  };

  const handleToggleWishlist = async () => {
    if (!isRenter) {
      showAppAlert({
        type: 'info',
        title: 'Renter account required',
        message: 'Log in as a renter to save cars to your wishlist.'
      });
      return;
    }

    if (wishlistBusy) {
      return;
    }

    try {
      setWishlistBusy(true);
      if (isWishlisted) {
        await renterService.removeFromWishlist(car.id);
        setIsWishlisted(false);
        showAppAlert({
          type: 'success',
          title: 'Removed from wishlist',
          message: 'The vehicle has been removed from your saved cars.'
        });
      } else {
        await renterService.addToWishlist(car.id);
        setIsWishlisted(true);
        showAppAlert({
          type: 'success',
          title: 'Added to wishlist',
          message: 'The vehicle has been added to your saved cars.'
        });
      }
    } catch (error) {
      showAppAlert({
        type: 'error',
        title: 'Wishlist update failed',
        message: error.message || 'We could not update your wishlist.'
      });
    } finally {
      setWishlistBusy(false);
    }
  };

  const refreshCar = async () => {
    const apiCar = await publicService.getCarById(id);
    setCar(mapApiCarToViewModel(apiCar));
  };

  const handleSubmitReview = async () => {
    if (!reviewEligibility?.allowed || !reviewEligibility?.bookingId) {
      return;
    }

    try {
      setReviewSubmitting(true);
      await renterService.createReview(id, {
        bookingId: reviewEligibility.bookingId,
        rating: Number(reviewForm.rating),
        reviewText: reviewForm.reviewText
      });
      showAppAlert({
        type: 'success',
        title: 'Review submitted',
        message: 'Thanks for rating this car.'
      });
      setReviewForm({
        rating: 5,
        reviewText: ''
      });
      setReviewEligibility({
        ...reviewEligibility,
        allowed: false,
        alreadyReviewed: true,
        message: 'You have already reviewed this car.'
      });
      await refreshCar();
    } catch (error) {
      showAppAlert({
        type: 'error',
        title: 'Review failed',
        message: error.message || 'We could not submit your review.'
      });
    } finally {
      setReviewSubmitting(false);
    }
  };

  if (loading || !car) return null;
  const galleryImages = car.images?.length ? car.images : [car.image];
  const activeImage = galleryImages[activeImageIndex] || galleryImages[0] || car.image;

  const handleOpenZoomAtIndex = (index) => {
    const targetIndex = Number.isInteger(index) ? index : activeImageIndex;
    const targetImage = galleryImages[targetIndex] || activeImage;
    if (!targetImage) {
      return;
    }
    setActiveImageIndex(targetIndex);
    setZoomOpen(true);
    setZoomScale(1);
    setZoomOrigin('50% 50%');
  };

  const handleZoomWheel = (event) => {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.2 : 0.2;
    setZoomScale((current) => Math.max(1, Math.min(4, Number((current + delta).toFixed(2)))));
  };

  const handleZoomMouseMove = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width) * 100;
    const y = ((event.clientY - bounds.top) / bounds.height) * 100;
    setZoomOrigin(`${Math.max(0, Math.min(100, x))}% ${Math.max(0, Math.min(100, y))}%`);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col font-sans">
      <SeoHead
        title={`${car.year ? `${car.year} ` : ''}${car.name} | Go Rides`}
        description={`${car.about?.slice(0, 140) || 'Book this premium rental on Go Rides.'}${car.about && car.about.length > 140 ? '...' : ''}`}
        canonicalPath={carPath(car.id,car.name)}
        image={car.image}
        type="product"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: `${car.year ? `${car.year} ` : ''}${car.name}`,
          image: car.images?.length ? car.images : [car.image],
          description: car.about || 'Premium rental car on Go Rides',
          brand: {
            '@type': 'Brand',
            name: 'Go Rides'
          },
          offers: {
            '@type': 'Offer',
            priceCurrency: 'RWF',
            price: String(car.price || 0),
            availability: 'https://schema.org/InStock',
            url: `${window.location.origin}${carPath(car.id,car.name)}`
          },
          ...(car.reviewCount > 0 && car.averageRating > 0 ? { aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: String(car.averageRating || 0),
            reviewCount: String(car.reviewCount || 0)
          }} : {})
        }}
      />
      <Navbar />

      <main className="flex-grow pt-4">
        {/* Breadcrumbs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <nav className="flex text-xs font-medium text-gray-400 gap-2">
            <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
            <span>/</span>
            <Link to="/browse" className="hover:text-gray-900 transition-colors">{car.category}s</Link>
            <span>/</span>
            <span className="text-gray-900">{car.name} {car.year}</span>
          </nav>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <div className="flex flex-col lg:flex-row gap-12">

            {/* Left Column: Details (66%) */}
            <div className="lg:w-2/3 space-y-10">

              {/* Image Gallery */}
              <div className="space-y-4">
                <div
                  className="relative aspect-[16/9] bg-gray-50 rounded-2xl overflow-hidden border border-gray-100 group cursor-zoom-in"
                  onClick={() => handleOpenZoomAtIndex(activeImageIndex)}
                >
                  <img
                    src={activeImage}
                    alt={car.name}
                    className="w-full h-full object-cover mix-blend-multiply p-8 transform-gpu transition-transform duration-500 ease-out group-hover:scale-110"
                  />
                  <div className="absolute bottom-6 left-6 flex gap-2">
                    <span className="bg-brand-black text-white text-[9px] font-black px-3 py-1.5 rounded-md uppercase tracking-widest shadow-lg">
                      PREMIUM
                    </span>
                    {(car.type === 'EV' || car.type === 'Electric') && (
                      <span className="bg-brand-black text-white text-[9px] font-black px-3 py-1.5 rounded-md uppercase tracking-widest shadow-lg">
                        ELECTRIC
                      </span>
                    )}
                  </div>
                  <div className="absolute top-4 right-4 rounded-full bg-white/90 backdrop-blur px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-gray-900 flex items-center gap-1.5 border border-gray-200 shadow-sm">
                    <ZoomIn className="w-3.5 h-3.5" />
                    Zoom
                  </div>
                </div>

                <div className="grid grid-cols-4 sm:grid-cols-5 gap-4">
                  {galleryImages.map((imageUrl, index) => (
                    <button
                      key={imageUrl + index}
                      type="button"
                      onClick={() => handleOpenZoomAtIndex(index)}
                      className={`aspect-[4/3] bg-gray-50 rounded-xl border flex items-center justify-center p-2 cursor-pointer transition-colors overflow-hidden ${
                        activeImageIndex === index
                          ? 'border-gray-900 ring-2 ring-gray-900/20'
                          : 'border-gray-100 hover:border-gray-900'
                      }`}
                    >
                      <img src={imageUrl} alt={`${car.name} ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Title Section */}
              <div className="flex justify-between items-start pt-6 border-t border-gray-100">
                <div>
                  <h1 className="text-3xl font-black text-gray-900 mb-2">{car.year} {car.name} - Booking</h1>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-500 font-semibold">
                      <MapPin className="w-4 h-4" />
                      <span>{car.cityName || 'Location pending'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-amber-600 font-semibold">
                      <Star className="w-4 h-4 fill-current" />
                      <span>{`${car.averageRating.toFixed(1)} (${car.reviewCount} reviews)`}</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-green-50 text-green-700 px-2.5 py-1 rounded-full text-[11px] font-bold border border-green-100">
                       <ShieldCheck className="w-3.5 h-3.5" />
                       Verified Listing
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button aria-label="Share this car" onClick={handleShare} className="p-2.5 rounded-full border border-gray-100 hover:bg-gray-50 transition-colors text-gray-500">
                    <Share2 className="w-5 h-5" />
                  </button>
                  <a className="text-xs font-bold underline" href={`https://wa.me/?text=${encodeURIComponent(`${car.name} on Go Rides: ${window.location.origin}${carPath(car.id,car.name)}`)}`} target="_blank" rel="noopener noreferrer" onClick={()=>trackDiscovery('share',car.id)}>WhatsApp</a>
                  <button className="text-xs font-bold underline" onClick={async()=>{try{await navigator.clipboard.writeText(`${window.location.origin}${carPath(car.id,car.name)}`);showAppAlert({type:'success',title:'Link copied',message:'Car link copied.'});trackDiscovery('share',car.id);}catch{showAppAlert({type:'info',title:'Car link',message:`${window.location.origin}${carPath(car.id,car.name)}`});}}}>Copy link</button>
                  <button
                    onClick={handleToggleWishlist}
                    className={`p-2.5 rounded-full border transition-colors ${
                      isWishlisted
                        ? 'border-red-100 bg-red-50 text-red-600'
                        : 'border-gray-100 hover:bg-gray-50 text-gray-500'
                    }`}
                  >
                    <Heart className={`w-5 h-5 ${isWishlisted ? 'fill-current' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Technical Specifications Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
                  <Zap className="w-5 h-5 text-gray-900 mb-3" />
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Engine</p>
                  <p className="text-sm font-bold text-gray-900 truncate">{car.engine}</p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
                  <Users className="w-5 h-5 text-gray-900 mb-3" />
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Seats</p>
                  <p className="text-sm font-bold text-gray-900">{car.seats} Adults</p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
                  <Settings className="w-5 h-5 text-gray-900 mb-3" />
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Trans</p>
                  <p className="text-sm font-bold text-gray-900">{car.transmission}</p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
                  <Battery className="w-5 h-5 text-gray-900 mb-3" />
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Category</p>
                  <p className="text-sm font-bold text-gray-900">{car.category}</p>
                </div>
              </div>

              {/* Features & Terms Grid */}
              <div className="grid md:grid-cols-2 gap-12 pt-6 border-t border-gray-100">
                <div>
                  <h3 className="text-sm font-black uppercase tracking-[0.2em] text-gray-400 mb-6">Rental Terms</h3>
                  <ul className="space-y-4">
                    {car.rentalTerms.map((term, i) => (
                      <li key={`${term.title}-${i}`} className="flex items-start gap-3 text-sm text-gray-600">
                        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-1" />
                        <div>
                          <p className="font-black text-gray-900">{term.title}</p>
                          {term.description ? <p className="text-gray-500 mt-1 leading-6">{term.description}</p> : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-black uppercase tracking-[0.2em] text-gray-400 mb-6">Included Features</h3>
                  <div className="space-y-6">
                    {car.specificationGroups.length > 0 ? (
                      car.specificationGroups.map((group) => (
                        <div key={group.label}>
                          <h4 className="text-xs font-black uppercase tracking-[0.18em] text-gray-500 mb-3">
                            {group.label}
                          </h4>
                          <ul className="space-y-3">
                            {group.items.map((item) => (
                              <li key={`${group.label}-${item}`} className="flex items-center gap-3 text-sm font-semibold text-gray-600">
                                <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No included features have been added yet.</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-gray-100">
                <div className="flex items-end justify-between gap-6 mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Ratings & Reviews</h3>
                    <p className="text-sm text-gray-500">
                      {car.reviewCount > 0
                        ? `${car.averageRating.toFixed(1)} average from ${car.reviewCount} verified renter reviews.`
                        : 'This car has not been reviewed yet.'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-amber-100 bg-amber-50 px-5 py-4 text-right">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-500 mb-1">Average Rating</p>
                    <div className="flex items-center gap-2 text-amber-700">
                      <Star className="w-5 h-5 fill-current" />
                      <span className="text-2xl font-black">{car.averageRating.toFixed(1)}</span>
                    </div>
                  </div>
                </div>

                {isRenter && (
                  <div className="mb-8 rounded-2xl border border-gray-100 bg-gray-50 p-6">
                    <div className="flex items-center justify-between gap-4 mb-4">
                      <div>
                        <h4 className="text-base font-black text-gray-900">Share your experience</h4>
                        <p className="text-sm text-gray-500 mt-1">
                          {reviewEligibility?.message || 'Only renters with completed bookings can review this car.'}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {[1, 2, 3, 4, 5].map((value) => (
                        <button
                          key={value}
                          type="button"
                          disabled={!reviewEligibility?.allowed}
                          onClick={() => setReviewForm((current) => ({ ...current, rating: value }))}
                          className={`flex items-center gap-1 rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                            reviewForm.rating >= value
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-white text-gray-400 border border-gray-200'
                          } ${!reviewEligibility?.allowed ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <Star className={`w-4 h-4 ${reviewForm.rating >= value ? 'fill-current' : ''}`} />
                          {value}
                        </button>
                      ))}
                    </div>

                    <textarea
                      value={reviewForm.reviewText}
                      disabled={!reviewEligibility?.allowed}
                      onChange={(e) => setReviewForm((current) => ({ ...current, reviewText: e.target.value }))}
                      placeholder="Tell future renters what stood out about this car."
                      rows={4}
                      className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 outline-none focus:border-gray-400"
                    />

                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        disabled={!reviewEligibility?.allowed || reviewSubmitting}
                        onClick={handleSubmitReview}
                        className="rounded-2xl bg-brand-black px-5 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-white disabled:opacity-50"
                      >
                        {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {car.reviews.length > 0 ? (
                    car.reviews.map((review, index) => (
                      <div key={`${review.reviewerName}-${review.createdAt}-${index}`} className="rounded-2xl border border-gray-100 bg-white p-5">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div>
                            <p className="text-sm font-black text-gray-900">{review.reviewerName}</p>
                            <p className="text-xs font-bold uppercase tracking-[0.16em] text-gray-400 mt-1">{review.createdAt}</p>
                          </div>
                          <div className="flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-sm font-black text-amber-700">
                            <Star className="w-4 h-4 fill-current" />
                            {review.rating}
                          </div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600">
                          {review.reviewText || 'Verified renter rated this car highly.'}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-6 py-10 text-center text-sm text-gray-500">
                      No reviews have been added for this car yet.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: Sidebar (33%) */}
            <div className="lg:w-1/3">
              <div className="sticky top-28 space-y-6">

                {/* Booking Card */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-xl p-6 sm:p-8">
                  <div className="flex items-end justify-between mb-8">
                    <div>
                      <span className="text-4xl font-black text-gray-900">{formatCurrency(car.price)}</span>
                      <span className="text-gray-400 font-bold ml-1">/ day</span>
                    </div>
                    <div className="bg-green-50 text-green-600 text-[10px] font-black px-3 py-1.5 rounded-md uppercase tracking-widest border border-green-100">
                      Instant Conf.
                    </div>
                  </div>

                  {/* Booking Inputs */}
                  <div className="space-y-4 mb-8">
                    {bookingError && (
                      <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm font-medium">
                        {bookingError}
                      </div>
                    )}
                    <div>
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Destination Location</label>
                      <div className="relative">
                        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          value={bookingDetails.destinationLocation}
                          onChange={(e) => setBookingDetails((current) => ({
                            ...current,
                            destinationLocation: e.target.value
                          }))}
                          placeholder="Type destination location"
                          className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-transparent rounded-xl text-sm font-bold focus:bg-white focus:border-gray-200 outline-none transition-all"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Start Date</label>
                        <div className="relative">
                          <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="date"
                            min={todayDateValue}
                            value={bookingDetails.pickupDate}
                            onChange={(e) => setBookingDetails((current) => ({
                              ...current,
                              pickupDate: e.target.value,
                              returnDate: current.returnDate && current.returnDate < e.target.value ? '' : current.returnDate
                            }))}
                            className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-transparent rounded-xl text-xs font-bold focus:bg-white focus:border-gray-200 outline-none transition-all cursor-pointer"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">End Date</label>
                        <div className="relative">
                          <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="date"
                            min={bookingDetails.pickupDate || todayDateValue}
                            value={bookingDetails.returnDate}
                            onChange={(e) => setBookingDetails((current) => ({ ...current, returnDate: e.target.value }))}
                            className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-transparent rounded-xl text-xs font-bold focus:bg-white focus:border-gray-200 outline-none transition-all cursor-pointer"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Extras / Driver Toggle */}
                  <div className="bg-gray-50 rounded-2xl p-5 mb-8 flex items-center justify-between group transition-colors">
                    <div className="flex gap-3 items-center">
                       <MapPin className="w-5 h-5 text-gray-400" />
                       <div>
                         <p className="text-sm font-bold text-gray-900 leading-none mb-1">Outside City Trip</p>
                         <p className="text-[10px] font-medium text-gray-400">
                           {car.allowOutsideCity ? `Add ${formatCurrency(car.outsideCityPrice)} extra` : 'This vehicle must stay within the selected destination area'}
                         </p>
                       </div>
                    </div>
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={bookingDetails.outsideCity}
                        disabled={!car.allowOutsideCity}
                        onChange={(e) => setBookingDetails((current) => ({ ...current, outsideCity: e.target.checked }))}
                        className="sr-only"
                      />
                      <div className={`w-11 h-6 rounded-full relative transition-colors ${bookingDetails.outsideCity ? 'bg-brand-black' : 'bg-gray-200'}`}>
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${bookingDetails.outsideCity ? 'left-6' : 'left-1'}`}></div>
                      </div>
                    </label>
                  </div>

                  {/* Cost Breakdown */}
                    <div className="space-y-4 mb-8">
                    <div className="flex justify-between text-sm font-medium">
                      <span className="text-gray-500">Rental Fee ({rentalDays} days)</span>
                      <span className="text-gray-900">{formatCurrency(basePrice)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium">
                      <span className="text-gray-500">Applied Pricing Tier</span>
                      <span className="text-gray-900">{appliedConfig?.label || '1 Day'}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPricingBreakdown(true)}
                      className="text-left text-xs font-black uppercase tracking-widest text-brand-black hover:underline"
                    >
                      View Calculation
                    </button>
                    {outsideCityFee > 0 && (
                      <div className="flex justify-between text-sm font-medium">
                        <span className="text-gray-500">Outside City Fee</span>
                        <span className="text-gray-900">{formatCurrency(outsideCityFee)}</span>
                      </div>
                    )}
                    {serviceFee > 0 && (
                      <div className="flex justify-between text-sm font-medium">
                        <span className="text-gray-500">Service Fee</span>
                        <span className="text-gray-900">{formatCurrency(serviceFee)}</span>
                      </div>
                    )}
                    <div className="pt-4 border-t border-gray-100 flex justify-between items-center">
                      <span className="text-base font-bold text-gray-900">Total Price</span>
                      <span className="text-2xl font-black text-gray-900">{formatCurrency(totalPrice)}</span>
                    </div>
                  </div>

                  {!!car.pricingConfigurations?.length && (
                    <div className="mb-8 rounded-2xl border border-gray-100 bg-gray-50 p-4">
                      <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-3">All pricing configurations</p>
                      <div className="space-y-2">
                        {car.pricingConfigurations.map((item) => (
                          <div key={item.pricingType} className="flex items-center justify-between rounded-2xl bg-white border border-gray-100 px-4 py-3">
                            <div>
                              <p className="text-sm font-black text-gray-900">{item.label}</p>
                              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mt-1">{item.rangeLabel}</p>
                            </div>
                            <span className="text-sm font-black text-gray-900">
                              {formatCurrency(item.amount)}{item.perDay ? ' / day' : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleBookNow}
                    className="w-full bg-brand-black text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                  >
                    Continue to Checkout
                  </button>
                  <CarEnquiry car={car} dates={bookingDetails} />
                </div>

              </div>
            </div>

          </div>
        </div>
      </main>

      <Footer />
      <PricingBreakdownModal
        isOpen={showPricingBreakdown}
        onClose={() => setShowPricingBreakdown(false)}
        rentalDays={rentalDays}
        breakdown={breakdown}
        basePrice={basePrice}
        outsideCityFee={outsideCityFee}
        serviceFee={serviceFee}
        totalPrice={totalPrice}
      />
      {zoomOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setZoomOpen(false)}
        >
          <div
            className="w-full max-w-6xl bg-[#111111] border border-white/10 rounded-2xl p-4 sm:p-6"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <p className="text-white text-xs font-black uppercase tracking-widest">Image Zoom</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setZoomScale((current) => Math.max(1, Number((current - 0.2).toFixed(2))))}
                  className="w-10 h-10 rounded-xl bg-white/10 text-white flex items-center justify-center border border-white/20 hover:bg-white/20 transition-colors"
                  aria-label="Zoom out"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setZoomScale((current) => Math.min(4, Number((current + 0.2).toFixed(2))))}
                  className="w-10 h-10 rounded-xl bg-white/10 text-white flex items-center justify-center border border-white/20 hover:bg-white/20 transition-colors"
                  aria-label="Zoom in"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setZoomOpen(false)}
                  className="w-10 h-10 rounded-xl bg-white text-gray-900 flex items-center justify-center border border-white hover:bg-gray-100 transition-colors"
                  aria-label="Close zoom"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div
              className="relative h-[72vh] bg-black rounded-2xl overflow-hidden border border-white/10 cursor-move"
              onWheel={handleZoomWheel}
              onMouseMove={handleZoomMouseMove}
            >
              <img
                src={activeImage}
                alt={`${car.name} zoomed`}
                className="w-full h-full object-contain select-none"
                style={{
                  transform: `scale(${zoomScale})`,
                  transformOrigin: zoomOrigin,
                  transition: 'transform 120ms ease-out'
                }}
                draggable={false}
              />
            </div>
            <p className="mt-3 text-xs text-gray-300 font-semibold">
              Scroll to zoom, move cursor to focus details, or use +/- controls.
            </p>
            <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
              {galleryImages.map((imageUrl, index) => (
                <button
                  key={`zoom-thumb-${index}`}
                  type="button"
                  onClick={() => {
                    setActiveImageIndex(index);
                    setZoomScale(1);
                    setZoomOrigin('50% 50%');
                  }}
                  className={`rounded-lg overflow-hidden border ${
                    activeImageIndex === index
                      ? 'border-white ring-2 ring-white/40'
                      : 'border-white/20 hover:border-white/60'
                  }`}
                >
                  <img src={imageUrl} alt={`${car.name} zoom thumb ${index + 1}`} className="w-full h-14 object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarDetails;
